import {
  AIRTIME_MAX_NAIRA,
  CABLE_SERVICE_FEE_NAIRA,
  COMPANY,
  DASH,
  EJOR_EXPIRY_HOURS,
  BANK_TRANSFER,
  ELECTRICITY_FEE,
  REPORT,
} from '../../config/company';
import { naira } from '../HomeShowcases';
import { LegalLayout } from './LegalLayout';

export function TermsPage() {
  return (
    <LegalLayout
      path="/terms"
      title="Terms of Service"
      intro={`These terms are an agreement between you and ${COMPANY.legalName}. By messaging Hethera you agree to them. If you do not agree, please do not use the service.`}
    >
      <h2>The service</h2>
      <p>
        Hethera lets you buy airtime, mobile data, cable TV subscriptions and electricity through
        WhatsApp, and pay with your own debit card. Hethera is not a bank. We do not hold deposits or
        keep a balance for you.
      </p>

      <h2>Who can use Hethera</h2>
      <p>You must be at least 18 years old and allowed to use the card you add.</p>

      <h2>Your account and PIN</h2>
      <p>
        Keep your phone and your transaction PIN private. You are responsible for any purchase
        confirmed with your PIN. If you think someone else knows your PIN, contact us straight away
        and we will lock your account.
      </p>

      <h2>Payments and fees</h2>
      <p>
        You pay for every purchase with the debit or credit card you saved through Paystack. Card is
        the only way to pay: we don't accept bank transfers, USSD, cash or wallet top-ups, and we
        will never ask you to send money to an account number.
      </p>
      <p>
        Airtime and data have no fee. Airtime is sold up to {naira(AIRTIME_MAX_NAIRA)} per purchase.
        Some data plans may not be available at times; we only offer the ones we can process.
      </p>
      <p>
        Cable TV purchases carry a service fee of {naira(CABLE_SERVICE_FEE_NAIRA)}, added to the plan
        price and charged to your card together with it.
      </p>
      <p>
        For electricity, the amount you enter is the amount charged to your card. A card processing
        fee of {ELECTRICITY_FEE.pct}% plus {naira(ELECTRICITY_FEE.flat)} ({ELECTRICITY_FEE.pct}% only below{' '}
        {naira(ELECTRICITY_FEE.flatFrom)}, and never more than {naira(ELECTRICITY_FEE.cap)}), rounded up
        to the next naira, is taken from that amount, and the rest is bought as electricity for your
        meter. For example, if you pay ₦5,000, you get ₦4,825 of electricity and the fee is ₦175. Your
        meter's minimum purchase applies to the electricity, not to what you pay.
      </p>
      {BANK_TRANSFER.live && (
        <p>
          You can also pay for electricity of {naira(BANK_TRANSFER.minimum)} or more by bank transfer
          to an account number we give you, which is yours for Hethera payments. A bank transfer fee
          of {BANK_TRANSFER.pct}%, never more than {naira(BANK_TRANSFER.cap)}, comes out of what you
          send instead of the card fee. You must send the exact amount we ask for, within the time we
          give. Money that doesn't match an order is refunded in full to the account it came from.
        </p>
      )}
      <p>
        If a purchase is refunded, the whole amount charged is refunded, including any fee. We may
        change these fees and limits; the amounts shown before you confirm are the ones that apply.
      </p>
      <p>
        Before you confirm a purchase, we show the full amount, including any fees. We may refuse or
        limit a transaction to prevent fraud. We monitor accounts for unusual activity, and our team
        may contact you or suspend an account while we review it.
      </p>

      <h2>Scheduled payments</h2>
      <p>
        When you set up a scheduled payment and confirm it with your PIN, you authorise Hethera to
        charge your saved card on each due date, for that bill and up to the maximum amount you set
        (plus the cable service fee; for electricity, the amount set is what is charged). Each due date is charged at most once. If a charge fails, we do not
        try again; you can pay it yourself with Pay now. We pause a schedule if your card is declined
        twice in a row, if you add a different card, or if the saved recipient's details change, and
        only you can resume it, with your PIN. You can pause, skip or cancel a schedule at any time by
        sending SCHEDULE in the chat.
      </p>

      <h2>Ejor</h2>
      <p>
        Ejor lets you ask another Hethera customer to pay a bill for you. You can only send requests
        to people who have turned Ejor on, and they choose whether to pay. A request expires after{' '}
        {EJOR_EXPIRY_HOURS} hours if it is not paid. If you pay a bill for someone else, the purchase is
        yours and is charged to your card, and the usual refund rules apply to you. You can turn Ejor
        off or block someone at any time. Do not use Ejor to pressure, mislead or harass anyone.
      </p>

      <h2>Dash gifts</h2>
      <p>
        A Dash gift is a payment you make now for one airtime or electricity purchase for another
        Hethera customer, made when they accept it. Airtime gifts follow the airtime limit above and
        have no fee. For an electricity gift, the card processing fee described above comes out of
        what you pay, and the person receiving it gets the rest as electricity. It is not stored value: it has no cash value and
        can't be withdrawn, split, transferred, or used for a different bill. The person receiving it has{' '}
        {DASH.responseDays} days from the delivery day to accept it. If they decline, don't accept in
        time, or it can't be delivered, the full amount, including the fee, is refunded to the card
        that paid for it. Refunds always go to the sender's card, never to the person receiving the
        gift. You can cancel a gift before its delivery day for a full refund. If we suspect fraud, or
        the payment for a gift is disputed with the bank, we may freeze the gift until we have checked
        it, and refund the sender instead of delivering it.
      </p>

      <h2>Spending reports</h2>
      <p>
        A spending report link is for you only. It works once and expires after {REPORT.linkHours}{' '}
        hours if it is not opened. Do not forward it. If you think someone else opened your report,
        reply REPORT STOP in the chat. Reports show the figures as they were when the report was made.
      </p>

      <h2>Check before you confirm</h2>
      <p>
        You are responsible for the phone number, meter number or smartcard number you give us. A
        purchase delivered to the details you confirmed cannot be reversed.
      </p>

      <h2>Acceptable use</h2>
      <p>
        You must not use Hethera for fraud or money laundering, to pay with a card you are not
        authorised to use, or to abuse or attack the service.
      </p>

      <h2>Liability</h2>
      <p>
        Mobile networks, cable companies and electricity distributors provide the services you buy. We
        are not responsible for their outages. If a purchase you paid for is not delivered, we will
        refund it under our <a href="/refund-policy">Refund Policy</a>.
      </p>

      <h2>Ending the service</h2>
      <p>
        You can stop using Hethera at any time and ask us to delete your data, as described on our{' '}
        <a href="/data-deletion">data deletion page</a>. We may suspend accounts used for fraud or in
        breach of these terms.
      </p>

      <h2>Governing law</h2>
      <p>These terms are governed by the laws of the Federal Republic of Nigeria.</p>
    </LegalLayout>
  );
}
